import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Chips from 'components/field/Chips';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import * as S from './CountriesManagement.styled';

const defaultColSpan = {
	md: 6
};

const fullColSpan = {
	sm: 12
};

export const CountriesManagementForm = NiceModal.create(({ id, mode, modalName }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const bankDetailsSchema = () =>
		yup
			.string()
			.nullable()
			.when(['bankName', 'ibanNumber', 'bankHolderName'], {
				is: (bankName, ibanNumber, bankHolderName) => !!bankName || !!bankHolderName || !!ibanNumber,
				then: (schema) => schema.required(),
				otherwise: (schema) => schema
			});

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					countryName: yup.string().nullable().required(),
					countryISO2: yup
						.string()
						.min(2)
						.max(2)
						.nullable()
						.required()
						.matches(/^(?=(?:.*[A-Z]){2})/, 'error.invalidCountryISO2'),
					countryCode: yup.string().nullable(),
					countryCodeAR: yup.string().nullable(),
					unitOfMeasurement: yup.string().nullable(),
					dateFormat: yup.string().nullable(),
					minimumBankTransferLimit: yup.number().nullable(),
					bankTransferAvailable: yup.boolean().nullable(),
					paymentGatewayType: yup.string().nullable(),
					bankTransferReceiverAccount: yup.string().nullable(),
					chargeCreditOptions: yup.array().of(yup.string()).nullable(),
					vatPercentage: yup.number().nullable(),
					minimumChargeAmount: yup.number().nullable(),
					currency: yup.string().nullable().required(),
					status: yup.boolean().nullable(),
					otoSMSProviderId: yup.number().nullable(),
					referralAvailable: yup.boolean().nullable(),
					refererAmount: yup.number().nullable(),
					refereeAmount: yup.number().nullable(),
					otoFeePerOrder: yup.number().nullable(),
					allowedCountries: yup.string().nullable(),
					monetizedIntroVideoURL: yup.string().nullable(),
					monetizedIntroVideoAmount: yup.number().nullable(),
					configJSON: yup.string().nullable().json(),
					mapSettings: yup.string().nullable().json(),
					featureBreakdownJSON: yup.string().nullable().json(),
					phoneCode: yup.string().nullable(),
					tags: yup.array().of(yup.string()).nullable(),
					bankName: yup.lazy(bankDetailsSchema),
					ibanNumber: yup.lazy(bankDetailsSchema),
					bankHolderName: yup.lazy(bankDetailsSchema),
					monetizedVideoEn: yup.string().nullable(),
					monetizedVideoAr: yup.string().nullable(),
					monetizedVideoTr: yup.string().nullable()
				})
				.required(),
		[]
	);

	const safeParse = (value) => {
		try {
			return JSON.parse(value);
		} catch {
			return null;
		}
	};

	const fillForm = async () => {
		const defaultValues = {
			name: null,
			shortCode: null,
			hasState: false,
			hasNeighbourhood: false,
			hasShortAddress: false,
			countryName: null,
			countryISO2: null,
			countryCode: null,
			countryCodeAR: null,
			unitOfMeasurement: null,
			dateFormat: null,
			minimumBankTransferLimit: null,
			bankTransferAvailable: null,
			paymentGatewayType: null,
			bankTransferReceiverAccount: null,
			chargeCreditOptions: [],
			vatPercentage: null,
			minimumChargeAmount: null,
			currency: null,
			status: false,
			otoSMSProviderId: null,
			referralAvailable: null,
			refererAmount: null,
			refereeAmount: null,
			otoFeePerOrder: null,
			allowedCountries: null,
			monetizedIntroVideoURL: null,
			monetizedIntroVideoAmount: null,
			configJSON: null,
			mapSettings: null,
			featureBreakdownJSON: null,
			phoneCode: null,
			tags: [],
			bankName: null,
			ibanNumber: null,
			bankHolderName: null,
			monetizedVideoEn: null,
			monetizedVideoAr: null,
			monetizedVideoTr: null
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const { data } = await axiosPrivate.post(ADMIN.COUNTRIES.GET_COUNTRY_DETAILS, { id });
			const bankDetails = safeParse(data?.bankTransferReceiverAccount);
			const monetizedVideos = safeParse(data?.monetizedIntroVideoURL);

			return {
				...defaultValues,
				...data,
				tags: data.tags ? data.tags.split(',').map((tag) => tag.trim()) : [],
				...(bankDetails && {
					bankName: bankDetails.bankName,
					ibanNumber: bankDetails.ibanNumber,
					bankHolderName: bankDetails.bankHolderName
				}),
				...(monetizedVideos && {
					monetizedVideoEn: monetizedVideos.en || null,
					monetizedVideoAr: monetizedVideos.ar || null,
					monetizedVideoTr: monetizedVideos.tr || null
				}),
				status: data.status === 'active',
				chargeCreditOptions: data.chargeCreditOptions ? data.chargeCreditOptions.split(',') : []
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (data) => {
		const apiPath = mode === 'edit' ? ADMIN.COUNTRIES.UPDATE_COUNTRY : ADMIN.COUNTRIES.ADD_COUNTRY;
		const payload = {
			...data,
			tags: data.tags?.length > 0 ? data.tags.join(',') : null,
			bankTransferReceiverAccount: data.bankName
				? JSON.stringify({
						...(data.bankName && { bankName: data.bankName }),
						...(data.ibanNumber && { ibanNumber: data.ibanNumber }),
						...(data.bankHolderName && { bankHolderName: data.bankHolderName })
					})
				: null,
			monetizedIntroVideoURL:
				data.monetizedVideoEn || data.monetizedVideoAr || data.monetizedVideoTr
					? JSON.stringify({
							...(data.monetizedVideoEn && { en: data.monetizedVideoEn }),
							...(data.monetizedVideoAr && { ar: data.monetizedVideoAr }),
							...(data.monetizedVideoTr && { tr: data.monetizedVideoTr })
						})
					: null,
			chargeCreditOptions: data.chargeCreditOptions?.length > 0 ? data.chargeCreditOptions.join(',') : null
		};
		await axiosPrivate
			.post(apiPath, payload)
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.COUNTRIES_MANAGEMENT));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'add':
				return 'addCountry';
			case 'edit':
				return 'editCountry';
			default:
				return 'viewCountry';
		}
	}, [mode]);

	const formatStringJSON = (key) => {
		try {
			if (!watch(key)) {
				return '-';
			}
			return JSON.stringify(JSON.parse(watch(key)), null, 4);
		} catch {
			return watch(key);
		}
	};

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-country">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<S.FormContainer>
					<Form formMethods={formMethods} borderless>
						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								countryDetails
							</Typography>
						</S.FormSubHeader>
						<FormItem label="countryName" colSpan={defaultColSpan} isLoading={isLoading} required>
							<StringInput name="countryName" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="countryISO2" colSpan={defaultColSpan} isLoading={isLoading} required>
							<StringInput name="countryISO2" viewMode={isViewMode} maxlength={2} />
						</FormItem>
						<FormItem label="countryCode" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="countryCode" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="countryCodeAR" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="countryCodeAR" viewMode={isViewMode} />
						</FormItem>

						<FormItem label="unitOfMeasurement" colSpan={defaultColSpan} isLoading={isLoading}>
							<EnumSelect
								name="unitOfMeasurement"
								enumName={ENUMS.UNIT_OF_MEASUREMENT}
								setValue={(value) => {
									setValue(`unitOfMeasurement`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`unitOfMeasurement`)}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="dateFormat" colSpan={defaultColSpan} isLoading={isLoading}>
							<EnumSelect
								name="dateFormat"
								enumName={ENUMS.V2_DATE_FORMATS}
								setValue={(value) => {
									setValue(`dateFormat`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`dateFormat`)}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="phoneCode" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="phoneCode" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="currency" colSpan={defaultColSpan} isLoading={isLoading} required>
							<EnumSelect
								name="currency"
								enumName={ENUMS.CURRENCY}
								setValue={(value) => {
									setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`currency`)}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="tags" childStyle={{ margin: 0 }} isLoading={isLoading}>
							<Chips
								value={watch('tags')}
								setValue={(value) => {
									setValue('tags', value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								viewMode={isViewMode}
								isLoading={isLoading}
							/>
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								countrySettings
							</Typography>
						</S.FormSubHeader>
						<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading}>
							<SwitchInput name="status" viewMode={isViewMode} />
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="referralAvailable" isLoading={isLoading}>
							<SwitchInput name="referralAvailable" viewMode={isViewMode} />
						</FormItem>

						<FormItem label="refererAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="refererAmount"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="refereeAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="refereeAmount"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="otoFeePerOrder" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="otoFeePerOrder"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="allowedCountries" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="allowedCountries" viewMode={isViewMode} />
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.5rem" fontWeight="500">
								monetizedIntroVideos
							</Typography>
						</S.FormSubHeader>

						<FormItem label="monetizedVideoAr" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="monetizedVideoAr" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="monetizedVideoEn" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="monetizedVideoEn" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="monetizedVideoTr" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="monetizedVideoTr" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="monetizedIntroVideoAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="monetizedIntroVideoAmount"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								bankAndPaymentInfo
							</Typography>
						</S.FormSubHeader>
						<FormItem colSpan={defaultColSpan} label="bankTransferAvailable" isLoading={isLoading}>
							<SwitchInput name="bankTransferAvailable" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="minimumBankTransferLimit" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="minimumBankTransferLimit"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="paymentGatewayType" colSpan={defaultColSpan} isLoading={isLoading}>
							<EnumSelect
								name="paymentGatewayType"
								enumName={ENUMS.PAYMENT_GATEWAY_TYPE}
								setValue={(value) => {
									setValue(`paymentGatewayType`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`paymentGatewayType`)}
								viewMode={isViewMode}
							/>
						</FormItem>

						<FormItem label="chargeCreditOptions" colSpan={defaultColSpan} isLoading={isLoading}>
							<Chips
								value={watch('chargeCreditOptions')}
								setValue={(value) => {
									if (isNaN(+value?.[value?.length - 1])) {
										value?.pop?.();
									}

									setValue('chargeCreditOptions', value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								viewMode={isViewMode}
								isLoading={isLoading}
							/>
						</FormItem>
						<FormItem label="vatPercentage" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="vatPercentage"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="minimumChargeAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="minimumChargeAmount"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.5rem" fontWeight="500">
								bankDetails
							</Typography>
						</S.FormSubHeader>
						<FormItem label="bankName" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="bankName" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="bankHolderName" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="bankHolderName" viewMode={isViewMode} />
						</FormItem>
						<FormItem label="ibanNumber" colSpan={defaultColSpan} isLoading={isLoading}>
							<StringInput name="ibanNumber" viewMode={isViewMode} />
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								configs
							</Typography>
						</S.FormSubHeader>
						<FormItem label="otoSMSProviderId" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								name="otoSMSProviderId"
								allowFloat={false}
								showArrows={false}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem label="configJSON" colSpan={fullColSpan} isLoading={isLoading}>
							{!isViewMode ? (
								<TextArea
									name="configJSON"
									placeholder="{ }"
									rows="8"
									className="json-text-area"
									maxlength={4096}
								/>
							) : (
								<Typography>
									<pre className="pre-text">{formatStringJSON('configJSON')}</pre>
								</Typography>
							)}
						</FormItem>
						<FormItem label="mapSettings" colSpan={fullColSpan} isLoading={isLoading}>
							{!isViewMode ? (
								<TextArea
									name="mapSettings"
									placeholder="{ }"
									rows="8"
									className="json-text-area"
									maxlength={4096}
								/>
							) : (
								<Typography>
									<pre className="pre-text">{formatStringJSON('mapSettings')}</pre>
								</Typography>
							)}
						</FormItem>
						<FormItem label="featureBreakdownJSON" colSpan={fullColSpan} isLoading={isLoading}>
							{!isViewMode ? (
								<TextArea
									name="featureBreakdownJSON"
									placeholder="{ }"
									rows="8"
									className="json-text-area"
									maxlength={12096}
								/>
							) : (
								<Typography>
									<pre className="pre-text">{formatStringJSON('featureBreakdownJSON')}</pre>
								</Typography>
							)}
						</FormItem>
					</Form>
				</S.FormContainer>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						label={mode === 'add' ? 'add' : 'update'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (error) => AppLogger.error(error))}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CountriesManagementForm;
